import gql from "graphql-tag";
import {useCallback} from "react";

import {useMutation} from "@repo/data-gql";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {Errors, KAError} from "@repo/logging";

import type {SetPromotionMutationType} from "./__graphql-types__/index.ts";

export const SET_PROMOTION_MUTATION: GqlOperation<
    SetPromotionMutationType["response"],
    SetPromotionMutationType["variables"]
> = gqlOp(gql`
    mutation SetPromotionMutation($promoName: String!) {
        recordPromo(promoName: $promoName) {
            error {
                code
            }
        }
    }
`);

export const useSetPromotionMutation = (
    promoName: string,
): (() => Promise<void>) => {
    const [mutate] = useMutation(SET_PROMOTION_MUTATION);
    const setPromotionMutation = useCallback(async () => {
        const result = await mutate({promoName});
        // If we have an error status or we got a successful response but
        // it has an error in it, then we throw an error.
        // We should never see the loading status here since this waits
        // for the mutation to be complete, and we the aborted status is
        // something we can ignore for this case.
        const error =
            result.status === "error"
                ? result.error
                : result.status === "success" && result.data.recordPromo?.error
                  ? result.data.recordPromo.error.code
                  : null;
        if (error) {
            throw new KAError(
                "Failed to record AI Guide promotion mutation",
                Errors.Internal,
                {cause: error},
            );
        }
    }, [mutate, promoName]);
    return setPromotionMutation;
};
